import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useState, useEffect, memo } from 'react';
import { Button, Icon, Spinner, Switch, Tooltip, Typography } from '@scale/scaleui';
import { CodeBlock } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/types';
import { LANGUAGE_OPTIONS as SUPPORTED_EXECUTION_LANGUAGES } from '@scale/shared/sphereEngine/types';
import cn from 'classnames';
import { CodeEditorField } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/CodeEditor';
import CodeEditorExecutionOutput from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/CodeEditorExecutionOutput';
import {
  CodeScratchpadState,
  useCodingScratchpadStore,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/stores/useCodingScratchpadStore';
import { CodeContextSidebar } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/CodeContextSidebar';
import useCodeExecutionState from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/hooks/useCodeExecutionState';
import { ICustomCodeEnvironment } from '@scale/shared/types/config';
import crypto from 'crypto';
import { LanguageDropdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/LanguageDropdown';
import { PrettyRenderComponents } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/experimental-code-block/components';
import { twMerge } from 'tailwind-merge';
import { makeStyles } from 'design-system/product/theme';

export const CodingScratchpad = memo(
  // eslint-disable-next-line complexity
  ({
    rawCode,
    originalLanguage,
    setOpen,
    codeBlocksInMarkdownText,
    codeBlockIndex,
    customCodeEnvironments,
  }: {
    rawCode: string;
    originalLanguage: string;
    setOpen: (open: boolean) => void;
    codeBlocksInMarkdownText: CodeBlock[];
    codeBlockIndex: number;
    customCodeEnvironments?: ICustomCodeEnvironment[];
  }) => {
    const classes = useStyles();
    const { setScratchpadCodeState, getScratchpadCodeState } = useCodingScratchpadStore();

    const storeKey = useMemo(
      () =>
        crypto
          .createHash('sha256')
          .update(`${rawCode}-${originalLanguage}-codingScratchpad}`)
          .digest('hex'),
      [rawCode, originalLanguage],
    );

    const defaultCustomCodeEnvironment =
      customCodeEnvironments?.find(env => env.language === originalLanguage) ?? undefined;

    const defaultScratchpadState: CodeScratchpadState = {
      code: rawCode,
      language: defaultCustomCodeEnvironment?.language ?? originalLanguage,
      selectedCustomCodeEnvironment: defaultCustomCodeEnvironment,
    };

    useEffect(() => {
      if (!getScratchpadCodeState(storeKey)) {
        setScratchpadCodeState(storeKey, defaultScratchpadState);
      }
    }, [storeKey, getScratchpadCodeState, setScratchpadCodeState]);

    const { code, language, selectedCustomCodeEnvironment } =
      getScratchpadCodeState(storeKey) ?? defaultScratchpadState;

    const canExecuteCode =
      !!SUPPORTED_EXECUTION_LANGUAGES.find(({ id }) => id === language) ||
      !!selectedCustomCodeEnvironment;
    const [contextDrawerOpen, setContextDrawerOpen] = useState<boolean>(
      codeBlocksInMarkdownText.length > 1,
    );
    const [warningBannerOpen, setWarningBannerOpen] = useState<boolean>(true);
    const {
      outputTabOpen,
      outputValue,
      tab,
      isFetching,
      fetchError,
      loadingTaskIdForLogging,
      handleExecuteCode,
      setOutputTabOpen,
      setTab,
    } = useCodeExecutionState({
      storeKey,
      code,
      language,
      customCodeEnvironment: selectedCustomCodeEnvironment,
    });

    const formattedResult = useMemo(() => {
      if (language !== 'json') {
        return { formattingSuccessful: false, formattedContent: code };
      }

      const formatterFn = formatter[language.toLowerCase()];

      if (formatterFn) {
        return formatterFn(code);
      }

      return { formattingSuccessful: false, formattedContent: code };
    }, [code, language]);

    const PrettyRenderComponent = PrettyRenderComponents[language];
    const { formattingSuccessful, formattedContent } = formattedResult;
    const canPrettyRender = !!PrettyRenderComponent && !!formattingSuccessful;
    const [showPrettyRender, setShowPrettyRender] = useState<boolean>(canPrettyRender);

    const handleCodeChange = (value: string) => {
      setScratchpadCodeState(storeKey, { code: value });
    };

    const handleLanguageChange = (newLanguage: string) => {
      setScratchpadCodeState(storeKey, {
        language: newLanguage,
      });
    };

    const handleClickOutsideClose = (e: any) => {
      if (e.target === e.currentTarget) {
        setOpen(false);
      }
    };

    return (
      <>
        <div
          className="fixed top-0 left-0 w-full h-full bg-utility-black/50 flex flex-col justify-center items-center z-[2] text-danger"
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            handleClickOutsideClose(e);
          }}
        >
          <div className="flex flex-row w-[80vw] h-[80vh]">
            <div
              className={twMerge(
                'flex flex-col z-[2]',
                !showPrettyRender && outputTabOpen ? 'w-9/12' : 'w-full',
              )}
            >
              <div className="flex flex-row rounded-t-lg h-16 justify-between bg-[#2d2d30] w-full z-50">
                <LanguageDropdown
                  selectMenuClassName="pl-1 pt-2"
                  selectMenuButtonClassName="py-0 h-12 w-40 [&>p]:m-0"
                  selectMenuOptionClassName="list-none w-40 py-0.5 px-0"
                  customCodeEnvironments={customCodeEnvironments}
                  selectedLanguage={language}
                  selectedCustomCodeEnvironment={selectedCustomCodeEnvironment}
                  handleLanguageChange={handleLanguageChange}
                  setSelectedCustomCodeEnvironment={env =>
                    setScratchpadCodeState(storeKey, {
                      selectedCustomCodeEnvironment: env,
                    })
                  }
                />
                {canExecuteCode && (
                  <div className="pt-3 pr-4">
                    <Button
                      className={cn('h-10', {
                        'w-[102px]': isFetching,
                        'w-24': !isFetching,
                      })}
                      onClick={() => handleExecuteCode()}
                      size="sm"
                      variant="primary"
                      disabled={isFetching || loadingTaskIdForLogging}
                    >
                      {isFetching ? (
                        <Spinner className="" size="xs" variant="primary" />
                      ) : (
                        <FontAwesomeIcon icon={'play'}></FontAwesomeIcon>
                      )}
                      <span className="pl-2">{isFetching ? 'Executing' : 'Run Code'}</span>
                    </Button>
                  </div>
                )}

                <div className="flex flex-row pr-2 pt-4">
                  {canPrettyRender && (
                    <Tooltip
                      asChild
                      side="top"
                      target={
                        <div>
                          <Switch
                            className="pr-3 pb-4"
                            checked={showPrettyRender}
                            onCheckedChange={e => setShowPrettyRender(e)}
                          />
                        </div>
                      }
                      text={
                        <div className="whitespace-pre-wrap bg-neutral-1000">
                          {showPrettyRender ? 'Show Standard View' : 'Show Pretty View'}
                        </div>
                      }
                      delayDuration={0.1}
                    />
                  )}
                  <Icon
                    icon={'copy'}
                    variant="white"
                    onClick={e => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(code);
                    }}
                    tooltipProps={{
                      side: 'top',
                    }}
                    tooltip="Copy Code"
                  />

                  {!showPrettyRender && !outputTabOpen && (
                    <Icon
                      icon={outputTabOpen ? 'arrow-to-left' : 'arrow-to-right'}
                      variant="white"
                      onClick={e => {
                        e.stopPropagation();
                        setOutputTabOpen(!outputTabOpen);
                      }}
                      tooltipProps={{
                        side: 'top',
                      }}
                      tooltip="Open Output/Errors Tab"
                    />
                  )}
                  <Icon
                    icon="xmark"
                    variant="white"
                    onClick={e => {
                      e.stopPropagation();
                      setOpen(false);
                    }}
                    tooltipProps={{
                      side: 'top',
                    }}
                    tooltip="Close Scratchpad"
                  />
                </div>
              </div>
              {warningBannerOpen && !showPrettyRender && (
                <div className="flex flex-row h-[4vh] bg-[#242424] px-2 justify-between items-center bg-neutral-9">
                  <div className="flex flex-row justify-center items-center gap-1">
                    <div>
                      <Icon icon="exclamation-circle" variant="white" size="sm" />
                    </div>
                    <Typography.Paragraph1 className="text-neutral-0 text-xs">
                      Changes made in this scratchpad won't be saved in your response.
                    </Typography.Paragraph1>
                  </div>
                  <div>
                    <Icon
                      icon="xmark"
                      variant="white"
                      onClick={e => {
                        e.stopPropagation();
                        setWarningBannerOpen(false);
                      }}
                      size="sm"
                    />
                  </div>
                </div>
              )}
              {showPrettyRender && canPrettyRender ? (
                <PrettyRenderComponent
                  className={twMerge(
                    'bg-neutral-900 overflow-auto text-neutral-0 flex-1 w-full whitespace-pre-line rounded-b-md',
                    classes.scrollbarStyles,
                  )}
                  content={formattedContent}
                  disableYScroll
                />
              ) : (
                <div className="flex flex-col flex-1 min-h-0">
                  <CodeEditorField
                    className="flex flex-row bg-[#2d2d30] flex-1 min-h-0"
                    value={code}
                    onKeyDown={e => {
                      if (e.code === 'Space') {
                        e.stopPropagation();
                      }
                    }}
                    onChange={handleCodeChange}
                    editorProps={{
                      language,
                      theme: 'vs-dark',
                    }}
                    // See https://microsoft.github.io/monaco-editor/docs.html#variables/editor.EditorOptions.html
                    editorOptions={{
                      fontSize: 14,
                      minimap: { enabled: true },
                      scrollBeyondLastLine: false,
                      padding: {
                        top: 19,
                        bottom: 8,
                      },
                      formatOnPaste: true,
                      formatOnType: true,
                      renderLineHighlight: 'none',
                      readOnly: false,
                      // Hovers are very whacky and take up the entire width of the screen - so we disable them for now
                      hover: { enabled: false },
                      // Inline suggestions take up a large proportion of the screen when the editor is in inline mode
                      // Thus we should only enable this in full-screen mode
                      inlineSuggest: { enabled: true },
                      readOnlyMessage: {
                        value: 'Editor is in read-only mode',
                      },
                    }}
                  />
                </div>
              )}

              {!showPrettyRender && (
                <CodeContextSidebar
                  className="z-[50]"
                  codeBlocks={codeBlocksInMarkdownText}
                  codeBlockIndex={codeBlockIndex}
                  language={language}
                  handleSetEditorCode={handleCodeChange}
                  handleOpen={(open: boolean) => setContextDrawerOpen(open)}
                  open={contextDrawerOpen}
                />
              )}
            </div>

            {/* Output Modal Full Screen */}
            {!showPrettyRender && outputTabOpen && (
              <CodeEditorExecutionOutput
                className="flex-1 min-w-0 h-full w-3/12"
                outputValue={outputValue ?? ''}
                setOutputTabOpen={setOutputTabOpen}
                tab={tab}
                setTab={setTab}
                fetchError={fetchError as boolean}
                fullScreenEnabled={true}
                fullWidthEnabled={false}
              />
            )}
          </div>
        </div>
      </>
    );
  },
);

interface FormatterResult {
  formattedContent: string;
  formattingSuccessful: boolean;
}

const formatter: Record<string, (input: string) => FormatterResult> = {
  json: formatJSON,
};

function formatJSON(input: string): FormatterResult {
  try {
    const parsedJson = JSON.parse(input);
    return {
      formattingSuccessful: true,
      formattedContent: JSON.stringify(parsedJson, null, 2),
    };
  } catch (error) {
    return {
      formattingSuccessful: false,
      formattedContent: input,
    };
  }
}

const useStyles = makeStyles(() => ({
  scrollbarStyles: {
    '& div': {
      scrollbarColor: 'rgba(248, 248, 242, 0.5) rgba(0, 0, 0, 0)',
      scrollbarWidth: 'thin',
    },
  },
}));
